import type { ShiftSegment, TimeRangeFinite } from "types/types";
import { PnlCategoryLabelsEnum } from "types/enums";
import {
  generateTimeZoneAwareDate,
  getDateOfCurrentWeekDayNum,
  ONE_DAY_OF_MS,
  ONE_HOUR_OF_MS,
  ONE_WEEK_OF_MS,
} from "utils/timeUtils";
import { range } from "lodash";
import {
  useCustomerJson,
  useWorkScheduleIdOnCurrentPage,
} from "utils/customHooks/genericHooks";
import { useFlightConfig } from "components/DataGetter/FlightConfig/useFlightConfig";
import { NO_SHIFT_SCHEDULED } from "components/Dashboard/range-selectors/steppable-range-selectors/ShiftSelector/shiftUtils";

// January 5th, 2021, 11:59PM Eastern
export const JAN_5_2021_1159_PM_EST = 1609891140000;

/**
 * Filter out shifts that have not completed or that start before startDateTimeToShowReports
 * Sort by most recent to least recent
 *
 * @param {{
 *   shifts: ShiftSegment[];
 *   startDateTimeToShowReports: number;
 *   shouldSkipNPS?: boolean;
 * }} {
 *   shifts,
 *   startDateTimeToShowReports
 *   shouldSkipNPS = true
 * }
 * @returns {ShiftSegment[]}
 */
export const getAndSortCompletedShifts = ({
  shifts,
  startDateTimeToShowReports,
  shouldSkipNPS = true,
}: {
  shifts: ShiftSegment[];
  startDateTimeToShowReports: number;
  shouldSkipNPS?: boolean;
}): ShiftSegment[] => {
  const now = Date.now();

  return shifts.length <= 0
    ? []
    : shifts
        .filter(({ range: timeRange, shift }) => {
          const hasShiftStarted = startDateTimeToShowReports <= timeRange.ge;
          const isHourOrLessBeforeShiftEnds =
            timeRange.lt - ONE_HOUR_OF_MS <= now;

          const isShiftWithinRange =
            hasShiftStarted && isHourOrLessBeforeShiftEnds;

          // If customer wants to skip NPS shifts, filter out the following
          const isNPSShift =
            shift.name &&
            [
              PnlCategoryLabelsEnum.notScheduled,
              NO_SHIFT_SCHEDULED,
              "No Shift Scheduled",
              "Staff Changeover",
              "Shift Change",
            ].includes(shift.name);
          const isNPSShiftAndShouldSkip = shouldSkipNPS && isNPSShift;
          return isShiftWithinRange && !isNPSShiftAndShouldSkip;
        })
        .sort((a, b) => b.range.ge - a.range.ge);
};

/**
 * Gets unix time of customer's chosen hour, minute and timezone
 * baselineDate is the date we should be changing the hours/minutes of
 *
 * @param {({
 *   hour: number | undefined;
 *   minute: number | undefined;
 *   timeZone: string;
 *   baselineDate?: Date;
 * })} {
 *   hour,
 *   minute,
 *   timeZone,
 *   baselineDate
 * }
 * @return {*}  {number}
 */
const getReportTimeMs = ({
  hour = 0,
  minute = 0,
  timeZone,
  baselineDate = new Date(),
}: {
  hour?: number;
  minute?: number;
  timeZone: string;
  baselineDate?: Date;
}): number => {
  const dateObj = baselineDate.setHours(hour, minute, 0, 0);
  return generateTimeZoneAwareDate(true, dateObj, timeZone, true).getTime();
};

/**
 * Get time ranges for daily reports
 *
 * @param {*} {
 *   dailyReportTimes,
 *   timeZone,
 *   lastShiftLt
 * }
 * @return {*}  {TimeRangeFinite[]}
 */
export const getDailyReportTimes = ({
  dailyReportTimes,
  timeZone,
  lastShiftLt,
  isTest = false,
}: {
  dailyReportTimes?: {
    startHour: number;
    startMinute: number;
    endHour?: number;
    endMinute?: number;
  };
  timeZone: string;
  lastShiftLt: number;
  isTest?: boolean;
}): TimeRangeFinite[] => {
  // Get report start unix time, with customer's timezone
  let dailyReportStartTime = getReportTimeMs({
    hour: dailyReportTimes?.startHour,
    minute: dailyReportTimes?.startMinute,
    timeZone,
    baselineDate: isTest ? new Date(JAN_5_2021_1159_PM_EST) : undefined,
  });

  const constraintToCheckDateAgainst = isTest
    ? JAN_5_2021_1159_PM_EST
    : Date.now();

  // Get report end unix time, with customer's timezone
  let dailyReportEndTime = getReportTimeMs({
    hour: dailyReportTimes?.endHour || dailyReportTimes?.startHour,
    minute: dailyReportTimes?.endMinute || dailyReportTimes?.startMinute,
    timeZone,
    baselineDate: isTest ? new Date(JAN_5_2021_1159_PM_EST) : undefined,
  });

  // If the report's end time is before the start time, we need to go to the next day
  if (dailyReportEndTime - dailyReportStartTime < 0) {
    dailyReportEndTime += ONE_DAY_OF_MS;
  }

  // If the report's end time is in the future, we need to go to the previous day
  while (dailyReportEndTime > constraintToCheckDateAgainst) {
    dailyReportEndTime -= ONE_DAY_OF_MS;
    dailyReportStartTime -= ONE_DAY_OF_MS;
  }

  // If report duration is 0, assume we want a 24 hour report
  const dailyReportDurationMs =
    dailyReportEndTime - dailyReportStartTime === 0
      ? ONE_DAY_OF_MS
      : dailyReportEndTime - dailyReportStartTime;

  // We want a list of lt's from the last report start time that passed, to as far as shifts from ShiftsGetter
  // go so the list 24 hour and EOS reports are aligned
  const dailyReportLtValues = range(
    dailyReportEndTime,
    lastShiftLt,
    -ONE_DAY_OF_MS // lt will always be 24 hours earlier than the previous lt
  );

  return dailyReportLtValues.map(lt => ({
    ge: lt - dailyReportDurationMs,
    lt,
  }));
};

/**
 * Get time ranges for last 5 weekly reports
 *
 * @param {*} {
 *   weeklyReportTimes,
 *   timeZone
 * }
 * @return {*}  {TimeRangeFinite[]}
 */
export const getWeeklyReportTimes = ({
  weeklyReportTimes,
  timeZone,
  isTest = false,
  calendarSelectedDay = null,
}: {
  weeklyReportTimes?: {
    startDayOfWeek: number;
    endDayOfWeek?: number;
    startHour: number;
    startMinute: number;
    endHour?: number;
    endMinute?: number;
    numOfWeeks?: number;
  };
  timeZone: string;
  isTest?: boolean;
  calendarSelectedDay?: ShiftSegment | null;
}): TimeRangeFinite[] => {
  // Find the day of the week for which the customer wants the weekly report to start on
  const startDayIdx = weeklyReportTimes?.startDayOfWeek ?? 0;
  const currentDate = calendarSelectedDay?.range.ge
    ? new Date(calendarSelectedDay.range.ge + ONE_WEEK_OF_MS)
    : null;
  const weekStartDate = getDateOfCurrentWeekDayNum({
    targetWeekDayIdx: startDayIdx,
    currentDate: isTest
      ? new Date(JAN_5_2021_1159_PM_EST)
      : (currentDate ?? undefined),
  });

  // Find the day of the week for which the customer wants the weekly report to end on
  const endDayIdx =
    weeklyReportTimes?.endDayOfWeek ?? weeklyReportTimes?.startDayOfWeek ?? 0;
  const weekEndDate = getDateOfCurrentWeekDayNum({
    targetWeekDayIdx:
      weeklyReportTimes?.endDayOfWeek ?? weeklyReportTimes?.startDayOfWeek ?? 0,
    currentDate: isTest
      ? new Date(JAN_5_2021_1159_PM_EST)
      : (currentDate ?? undefined),
  });

  // Get report start unix time, with customer's timezone
  let weeklyReportStartTime = getReportTimeMs({
    hour: weeklyReportTimes?.startHour,
    minute: weeklyReportTimes?.startMinute,
    timeZone,
    baselineDate: weekStartDate,
  });

  // Get report end unix time, with customer's timezone
  let weeklyReportEndTime = getReportTimeMs({
    hour: weeklyReportTimes?.endHour ?? weeklyReportTimes?.startHour,
    minute: weeklyReportTimes?.endMinute ?? weeklyReportTimes?.startMinute,
    timeZone,
    baselineDate: weekEndDate,
  });

  const constraintToCheckDateAgainst = isTest
    ? JAN_5_2021_1159_PM_EST
    : Date.now();

  // If startDayIdx >= endDayIdx, then startDayOfWeek >= endDayOfWeek from CUSTOMERS_REPORT_TIMES
  // This means we need the start date of the report to go back a week
  weeklyReportStartTime -= startDayIdx >= endDayIdx ? ONE_WEEK_OF_MS : 0;

  // If the report's end time is in the future, we need to go to the previous week
  while (weeklyReportEndTime > constraintToCheckDateAgainst) {
    weeklyReportEndTime -= ONE_WEEK_OF_MS;
    weeklyReportStartTime -= ONE_WEEK_OF_MS;
  }

  // If the report duration is 0, assume we want a full week report (7 days)
  const weeklyReportDurationMs =
    weeklyReportEndTime - weeklyReportStartTime === 0
      ? ONE_WEEK_OF_MS
      : weeklyReportEndTime - weeklyReportStartTime;

  const weekLtXWeeksBack =
    weeklyReportEndTime - ONE_WEEK_OF_MS * (weeklyReportTimes?.numOfWeeks ?? 5);

  // We want a list of lt's for the last X weeks (default to 5)
  const weeklyReportLtValues = range(
    weeklyReportEndTime,
    weekLtXWeeksBack,
    -ONE_WEEK_OF_MS // lt will always be 1 week (7 days) earlier than the previous lt
  );

  return weeklyReportLtValues.map(lt => ({
    ge: lt - weeklyReportDurationMs,
    lt,
  }));
};

/** Get timezone from flightConfig.reportConfig[workScheduleId], fallback first to customer json, then Eastern */
export function useReportConfigTimeZone() {
  const customerJson = useCustomerJson();
  const [flightConfig] = useFlightConfig();
  const workScheduleId = useWorkScheduleIdOnCurrentPage();
  const reportConfig = flightConfig?.reportConfig?.[workScheduleId];
  return (
    reportConfig?.timeZone ??
    // deprecated -- prefer `timeZone` in flightConfig.reportConfig[myWorkScheduleId]
    reportConfig?.timezone ??
    customerJson?.timeZone ??
    null
  );
}
